import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IdName } from 'src/app/shared/models/id-name';
import { IDeliveryAddressResponse } from '../../models/delivery-addresses/delivery-address.model';
import { DeliveryAddressApiService } from '../../services/delivery-address-api.service';
import {
  CreateDeliveryAddress,
  DeleteDeliveryAddress,
  LoadDeliveryAddressById,
  LoadDeliveryAddresses,
  LoadDeliveryAddressList,
  UpdateDeliveryAddress,
  ClearCurrentDeliveryAddress,
} from './delivery-address.actions';

export interface DeliveryAddressStateModel {
  deliveryAddresses: IDeliveryAddressResponse[];
  deliveryAddressList: IdName[];
  deliveryAddressesTotalPages: number;
  currentDeliveryAddress: IDeliveryAddressResponse;
}

const DELIVERY_ADDRESS_STATE_TOKEN = new StateToken<DeliveryAddressStateModel>('deliveryAddresses');

@State<DeliveryAddressStateModel>({
  name: DELIVERY_ADDRESS_STATE_TOKEN,
  defaults: {
    deliveryAddresses: [],
    deliveryAddressList: [],
    deliveryAddressesTotalPages: 0,
    currentDeliveryAddress: <IDeliveryAddressResponse>{},
  },
})
@Injectable()
export class DeliveryAddressState {
  constructor(private service: DeliveryAddressApiService) {}

  @Action(LoadDeliveryAddresses, { cancelUncompleted: true })
  loadDeliveryAddresses(
    { getState, setState }: StateContext<DeliveryAddressStateModel>,
    { filter }: LoadDeliveryAddresses
  ) {
    return this.service.getPagedDeliveryAddresses(filter).pipe(
      tap((response: ApiResponse<IDeliveryAddressResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          deliveryAddresses: response.data,
          deliveryAddressesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadDeliveryAddressList, { cancelUncompleted: true })
  loadDeliveryAddressList(
    ctx: StateContext<DeliveryAddressStateModel>,
    { departmentId }: LoadDeliveryAddressList
  ) {
    return this.service.getDeliveryAddressList(departmentId).pipe(
      tap((deliveryAddressList: IdName[]) => {
        ctx.setState(patch({ deliveryAddressList }));
      })
    );
  }

  @Action(LoadDeliveryAddressById, { cancelUncompleted: true })
  loadDeliveryAddressById(
    ctx: StateContext<DeliveryAddressStateModel>,
    { id }: LoadDeliveryAddressById
  ) {
    return this.service.getDeliveryAddressById(id).pipe(
      tap((currentDeliveryAddress: IDeliveryAddressResponse) => {
        ctx.setState(patch({ currentDeliveryAddress }));
      })
    );
  }

  @Action(CreateDeliveryAddress)
  @OnSuccess({ url: AdminOrderRoutes.DeliveryAddresses, message: 'deliveryAddressCreated' })
  createDeliveryAddress(
    ctx: StateContext<DeliveryAddressStateModel>,
    { deliveryAddress }: CreateDeliveryAddress
  ) {
    return this.service.createDeliveryAddress(deliveryAddress);
  }

  @Action(UpdateDeliveryAddress)
  @OnSuccess({ url: AdminOrderRoutes.DeliveryAddresses, message: 'deliveryAddressUpdated' })
  updateDeliveryAddress(
    ctx: StateContext<DeliveryAddressStateModel>,
    { deliveryAddress }: UpdateDeliveryAddress
  ) {
    return this.service.updateDeliveryAddress(deliveryAddress);
  }

  @Action(DeleteDeliveryAddress)
  @OnSuccess({
    url: AdminOrderRoutes.DeliveryAddresses,
    message: 'deliveryAddressDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteDeliveryAddress(
    ctx: StateContext<DeliveryAddressStateModel>,
    { deliveryAddressId }: DeleteDeliveryAddress
  ) {
    return this.service.deleteDeliveryAddress(deliveryAddressId);
  }

  @Action(ClearCurrentDeliveryAddress)
  clearCurrentDeliveryAddress(ctx: StateContext<DeliveryAddressStateModel>) {
    ctx.setState(patch({ currentDeliveryAddress: <IDeliveryAddressResponse>{} }));
  }

  @Selector()
  static getPagedDeliveryAddresses(state: DeliveryAddressStateModel): IDeliveryAddressResponse[] {
    return state.deliveryAddresses;
  }

  @Selector()
  static getDeliveryAddressList(state: DeliveryAddressStateModel): IdName[] {
    return state.deliveryAddressList;
  }

  @Selector()
  static getDeliveryAddressesTotalPages(state: DeliveryAddressStateModel): number {
    return state.deliveryAddressesTotalPages;
  }

  @Selector()
  static getCurrentDeliveryAddress(state: DeliveryAddressStateModel): IDeliveryAddressResponse {
    return state.currentDeliveryAddress;
  }
}
